import lodashGet from "lodash/get";
import Geohash from "latlon-geohash";
import {
  featureCollection,
  feature,
  polygon,
  bbox,
  center,
  area,
  buffer,
  booleanIntersects,
} from "@turf/turf";

import { GRIDS_STABILITY_TYPES } from "@/common/constants/grids";
import { LAT_LNG_PRECISION, MAP_PRECISIONS } from "@/common/constants/maps";
import {
  CITIES_JSON_URL,
  COUNTRIES_JSON_URL,
  REGIONS_JSON_URL,
  COUNTIES_JSON_URL,
} from "@/common/constants/api";
import { httpGet } from "@/services/api.service";
import { getCityObjectNearGeohash } from "@/services/grids/grids.service";

export { _getLocationByCode as getLocationByCode };
export { _getAllRegions as getAllRegions };
export { _getAllCountries as getAllCountries };
export { _getCountryByCode as getCountryByCode };
export { _getAllCities as getAllCities };
export { _getCityByCode as getCityByCode };
export { _getAllCounties as getAllCounties };
export { _getCountyByCode as getCountyByCode };
export { _getGeohashFeature as getGeohashFeature };
export { _getGeohashByCode as getGeohashByCode };
export { _getGeohashByGeometry as getGeohashByGeometry };
export { _getBboxFromFeatures as getBboxFromFeatures };
export { _getCenterFromFeatures as getCenterFromFeatures };
export { _getCenterLngLatFromFeature as getCenterLngLatFromFeature };
export { _getBoundsString as getBoundsString };
export { _getBoundsStringSWNE as getBoundsStringSWNE };
export { _getBoundsStringFromGeometry as getBoundsStringFromGeometry };
export { _getBoundsStringFromGeometrySWNE as getBoundsStringFromGeometrySWNE };
export { _formatLatLng as formatLatLng };
export { _formatDegrees as formatDegrees };
export { _getSafeSWNEString as getSafeSWNEString };
export { _getSafeLatLng as getSafeLatLng };
export { _getSafeBounds as getSafeBounds };
export { _getPrecision as getPrecision };
export { _getFeatureArea as getFeatureArea };
export { _getBboxFeatureByBounds as getBboxFeatureByBounds };
export { _isBoundsStringEqual as isBoundsStringEqual };
export { _filterFeaturesToMapExtent as filterFeaturesToMapExtent };
export { _convertPolygonsToMultiPolygon as convertPolygonsToMultiPolygon };

const _getLocationByCode = async (type, code) => {
  let location;
  if (type === GRIDS_STABILITY_TYPES.country) {
    location = await _getCountryByCode(code);
  } else if (type === GRIDS_STABILITY_TYPES.city) {
    location = await _getCityByCode(code);
  } else if (type === GRIDS_STABILITY_TYPES.county) {
    location = await _getCountyByCode(code);
  } else if (type === GRIDS_STABILITY_TYPES.region) {
    let loc = _getGeohashFeature(code);
    const RESULTS = await getCityObjectNearGeohash(code, 100, true);
    if (
      lodashGet(RESULTS, "distance") &&
      lodashGet(RESULTS, "closest.properties.displayName") &&
      lodashGet(RESULTS, "closest.properties.countryName")
    ) {
      loc.properties.countryCode = RESULTS.closest.properties.country;
      loc.properties.countryName = RESULTS.closest.properties.countryName;
      loc.properties.cityName = null;
      if (RESULTS.distance < 100) {
        loc.properties.cityName = RESULTS.closest.properties.displayName;
      }
    }
    if (lodashGet(RESULTS, "closest.properties.countryWithin")) {
      loc.properties.countryWithin = RESULTS.closest.properties.countryWithin;
    }
    location = {
      data: loc,
    };
    location = JSON.stringify(location);
  } else {
    console.error("No type passed to getLocationByCode in map/utilities.js");
  }
  return JSON.parse(location);
};

const _getAllRegions = async () => {
  try {
    const RESULT = await httpGet({ url: REGIONS_JSON_URL });
    const REGIONS = JSON.parse(RESULT);
    if (
      lodashGet(REGIONS, "data.features") &&
      Array.isArray(REGIONS.data.features) &&
      REGIONS.data.features.length
    ) {
      return REGIONS.data.features;
    }
    return null;
  } catch (error) {
    console.error("Unable to retrieve regions", error);
    return false;
  }
};

const _getAllCountries = async () => {
  try {
    const RESULT = await httpGet({ url: COUNTRIES_JSON_URL });
    const COUNTRIES = JSON.parse(RESULT);
    if (
      lodashGet(COUNTRIES, "data.features") &&
      Array.isArray(COUNTRIES.data.features) &&
      COUNTRIES.data.features.length
    ) {
      return COUNTRIES.data.features;
    }
    return null;
  } catch (error) {
    console.error("Unable to retrieve countries", error);
    return false;
  }
};

const _getCountryByCode = async (code) => {
  try {
    const COUNTRIES = await _getAllCountries();
    let res = COUNTRIES.find((obj) => {
      return (
        (obj.properties.iso2 &&
          obj.properties.iso2.toUpperCase() === code.toUpperCase()) ||
        (obj.properties.iso3 &&
          obj.properties.iso3.toUpperCase() === code.toUpperCase()) ||
        (obj.properties.code &&
          obj.properties.code.toUpperCase() === code.toUpperCase())
      );
    });
    res = {
      data: res,
    };
    return JSON.stringify(res);
  } catch (error) {
    console.error("Unable to retrieve country", error);
    return false;
  }
};

const _getAllCities = async () => {
  try {
    const RESULT = await httpGet({ url: CITIES_JSON_URL });
    const CITIES = JSON.parse(RESULT);
    if (
      lodashGet(CITIES, "data.features") &&
      Array.isArray(CITIES.data.features) &&
      CITIES.data.features.length
    ) {
      return CITIES.data.features;
    }
    return null;
  } catch (error) {
    console.error("Unable to retrieve cities", error);
    return false;
  }
};

const _getCityByCode = async (code) => {
  try {
    const CITIES = await _getAllCities();
    let res = CITIES.find((obj) => {
      return (
        obj.properties.code &&
        obj.properties.code.toUpperCase() === code.toUpperCase()
      );
    });
    res = {
      data: res,
    };
    return JSON.stringify(res);
  } catch (error) {
    console.error("Unable to retrieve city", error);
    return false;
  }
};

const _getAllCounties = async (params) => {
  try {
    params = params || {};
    params.url = COUNTIES_JSON_URL;
    const RESULT = await httpGet(params);
    const COUNTIES = JSON.parse(RESULT);
    if (
      lodashGet(COUNTIES, "data.features") &&
      Array.isArray(COUNTIES.data.features) &&
      COUNTIES.data.features.length
    ) {
      return COUNTIES.data.features;
    }
    return null;
  } catch (error) {
    console.error("Unable to retrieve counties", error);
    return false;
  }
};

const _getCountyByCode = async (code) => {
  try {
    const COUNTIES = await _getAllCounties();
    let res = COUNTIES.find((obj) => {
      return (
        obj.properties.code &&
        obj.properties.code.toUpperCase() === code.toUpperCase()
      );
    });
    res = {
      data: res,
    };
    return JSON.stringify(res);
  } catch (error) {
    console.error("Unable to retrieve county", error);
    return false;
  }
};

const _getGeohashFeature = (geohash) => {
  try {
    const BOUNDS = Geohash.bounds(geohash);
    const MINX = parseFloat(BOUNDS.sw.lat.toFixed(7));
    const MINY = parseFloat(BOUNDS.ne.lon.toFixed(7));
    const MAXX = parseFloat(BOUNDS.ne.lat.toFixed(7));
    const MAXY = parseFloat(BOUNDS.sw.lon.toFixed(7));

    const GEOMETRY = {
      type: "Polygon",
      coordinates: [
        [
          [MAXY, MAXX],
          [MINY, MAXX],
          [MINY, MINX],
          [MAXY, MINX],
          [MAXY, MAXX],
        ],
      ],
    };

    return feature(GEOMETRY);
  } catch (error) {
    console.error("Unable to retrieve geohash", error);
    return false;
  }
};

const _getGeohashByCode = (geohash) => {
  const BOUNDS = Geohash.bounds(geohash);
  const CENTER = Geohash.decode(geohash);

  const MINX = parseFloat(BOUNDS.sw.lat.toFixed(7));
  const MINY = parseFloat(BOUNDS.ne.lon.toFixed(7));
  const MAXX = parseFloat(BOUNDS.ne.lat.toFixed(7));
  const MAXY = parseFloat(BOUNDS.sw.lon.toFixed(7));

  const GEOMETRY = [
    [
      [MAXY, MAXX],
      [MINY, MAXX],
      [MINY, MINX],
      [MAXY, MINX],
      [MAXY, MAXX],
    ],
  ];

  // need to create an api endpoint that reverse codes a lat/lng to
  // return city/country. it should also allow a bounds to be passed
  // to it so that it returns all city/countries the bounds touches.

  const PROPERTIES = {
    center: CENTER,
    locationName: "",
  };

  const POLYGON = polygon(GEOMETRY, PROPERTIES);

  return POLYGON;
};

const _getGeohashByGeometry = (geometry, precision) => {
  precision = precision ? precision : 6;
  const COORDS = lodashGet(geometry, "coordinates");
  if (COORDS && Array.isArray(COORDS) && COORDS[0] && COORDS[1]) {
    const GEOHASH = Geohash.encode(COORDS[1], COORDS[0], precision);
    return GEOHASH;
  }
  return null;
};

const _getBboxFromFeatures = (features) => {
  const COLLECTION = featureCollection(features);
  return bbox(COLLECTION); // returned: array [minX, minY, maxX, maxY]
};

const _getCenterFromFeatures = (features) => {
  const COLLECTION = featureCollection(features);
  return center(COLLECTION); // returned: point feature
};

const _getCenterLngLatFromFeature = (feature) => {
  let lng = null;
  let lat = null;
  if (
    feature &&
    feature.geometry &&
    feature.geometry.coordinates &&
    Array.isArray(feature.geometry.coordinates)
  ) {
    lng = feature.geometry.coordinates[0];
    lat = feature.geometry.coordinates[1];
  }
  return {
    lng: lng,
    lat: lat,
  };
};

const _getSafeSWNEString = (arr) => {
  const WEST_SAFE = !isNaN(arr[0]) ? _getSafeLatLng(arr[0], true) : null;
  const SOUTH_SAFE = !isNaN(arr[1]) ? _getSafeLatLng(arr[1], false) : null;
  const EAST_SAFE = !isNaN(arr[2]) ? _getSafeLatLng(arr[2], true) : null;
  const NORTH_SAFE = !isNaN(arr[3]) ? _getSafeLatLng(arr[3], false) : null;
  return `${SOUTH_SAFE},${WEST_SAFE},${NORTH_SAFE},${EAST_SAFE}`;
};

const _getSafeSWNE = (params) => {
  const WEST = params.west;
  const SOUTH = params.south;
  const EAST = params.east;
  const NORTH = params.north;

  const WEST_SAFE = !isNaN(WEST) ? _getSafeLatLng(WEST, true) : null;
  const SOUTH_SAFE = !isNaN(SOUTH) ? _getSafeLatLng(SOUTH, false) : null;
  const EAST_SAFE = !isNaN(WEST) ? _getSafeLatLng(EAST, true) : null;
  const NORTH_SAFE = !isNaN(WEST) ? _getSafeLatLng(NORTH, false) : null;

  return {
    west: WEST_SAFE,
    south: SOUTH_SAFE,
    east: EAST_SAFE,
    north: NORTH_SAFE,
  };
};

const _checkBoundsPrecision = (bounds, precision) => {
  let south = bounds.south.toFixed(precision);
  let north = bounds.north.toFixed(precision);
  let west = bounds.west.toFixed(precision);
  let east = bounds.east.toFixed(precision);

  if ((precision < 8 && south === north) || west === east) {
    return _checkBoundsPrecision(bounds, precision + 1);
  }
  return {
    west: west,
    south: south,
    east: east,
    north: north,
  };
};

const _getBoundsString = (map) => {
  const BOUNDS = map.getBounds();
  const SOUTH = BOUNDS.getSouth();
  const WEST = BOUNDS.getWest();
  const NORTH = BOUNDS.getNorth();
  const EAST = BOUNDS.getEast();

  const SAFE_SWNE = _getSafeSWNE({
    west: WEST,
    south: SOUTH,
    east: EAST,
    north: NORTH,
  });

  return (
    SAFE_SWNE.west.toFixed(LAT_LNG_PRECISION) +
    "," +
    SAFE_SWNE.south.toFixed(LAT_LNG_PRECISION) +
    "," +
    SAFE_SWNE.east.toFixed(LAT_LNG_PRECISION) +
    "," +
    SAFE_SWNE.north.toFixed(LAT_LNG_PRECISION)
  );
};

const _getBoundsStringSWNE = (map) => {
  const BOUNDS = map.getBounds();
  const SOUTH = BOUNDS.getSouth();
  const WEST = BOUNDS.getWest();
  const NORTH = BOUNDS.getNorth();
  const EAST = BOUNDS.getEast();

  const SAFE_SWNE = _getSafeSWNE({
    west: WEST,
    south: SOUTH,
    east: EAST,
    north: NORTH,
  });

  return (
    SAFE_SWNE.south.toFixed(LAT_LNG_PRECISION) +
    "," +
    SAFE_SWNE.west.toFixed(LAT_LNG_PRECISION) +
    "," +
    SAFE_SWNE.north.toFixed(LAT_LNG_PRECISION) +
    "," +
    SAFE_SWNE.east.toFixed(LAT_LNG_PRECISION)
  );
};

const _getBoundsStringFromGeometry = (geometry) => {
  const FEATURE = feature(geometry);
  const BBOX = bbox(FEATURE);
  const SOUTH = BBOX[1];
  const WEST = BBOX[0];
  const NORTH = BBOX[3];
  const EAST = BBOX[2];

  const SAFE_SWNE = _getSafeSWNE({
    west: WEST,
    south: SOUTH,
    east: EAST,
    north: NORTH,
  });

  return (
    SAFE_SWNE.west.toFixed(LAT_LNG_PRECISION) +
    "," +
    SAFE_SWNE.south.toFixed(LAT_LNG_PRECISION) +
    "," +
    SAFE_SWNE.east.toFixed(LAT_LNG_PRECISION) +
    "," +
    SAFE_SWNE.north.toFixed(LAT_LNG_PRECISION)
  );
};

const _getBoundsStringFromGeometrySWNE = (geometry) => {
  const FEATURE = feature(geometry);
  const BBOX = bbox(FEATURE);
  const SOUTH = BBOX[1];
  const WEST = BBOX[0];
  const NORTH = BBOX[3];
  const EAST = BBOX[2];

  const SAFE_SWNE = _getSafeSWNE({
    west: WEST,
    south: SOUTH,
    east: EAST,
    north: NORTH,
  });

  return (
    SAFE_SWNE.south.toFixed(LAT_LNG_PRECISION) +
    "," +
    SAFE_SWNE.west.toFixed(LAT_LNG_PRECISION) +
    "," +
    SAFE_SWNE.north.toFixed(LAT_LNG_PRECISION) +
    "," +
    SAFE_SWNE.east.toFixed(LAT_LNG_PRECISION)
  );
};

const _filterFeaturesToMapExtent = (map, features, bufferDist, bufferUnit) => {
  let catcher = _getBboxFeatureByBounds(_getBoundsStringSWNE(map).split(","));

  if (bufferDist && bufferUnit) {
    catcher = buffer(catcher, bufferDist, { units: bufferUnit });
  }

  let filtered = features.filter((feature) => {
    return booleanIntersects(catcher, feature);
  });

  return filtered;
};

const _formatLatLng = (arr) => {
  if (arr && Array.isArray(arr)) {
    return `${arr[1].toFixed(LAT_LNG_PRECISION)}, ${arr[0].toFixed(
      LAT_LNG_PRECISION
    )}`;
  }
  return "";
};

const _formatDegrees = (arr) => {
  if (arr && Array.isArray(arr)) {
    let x = "W";
    if (arr[0] > 0) {
      x = "E";
    }
    let y = "S";
    if (arr[1] > 0) {
      y = "N";
    }
    return `${arr[1].toFixed(LAT_LNG_PRECISION)}º ${y}, ${arr[0].toFixed(
      LAT_LNG_PRECISION
    )}º ${x}`;
  }
  return "";
};

const _getSafeLatLng = (value, isLng) => {
  if (!isNaN(value)) {
    if (isLng) {
      if (value < -180) {
        value = -180;
      } else if (value > 180) {
        value = 180;
      }
    } else {
      if (value < -90) {
        value = -90;
      } else if (value > 90) {
        value = 90;
      }
    }
  }
  return value;
};

const _getSafeBounds = (bounds) => {
  // bounds should be a string like: WEST, SOUTH, EAST, NORTH
  if (bounds && typeof bounds === "string") {
    const PARTS = bounds.split(",");
    if (PARTS && Array.isArray(PARTS) && PARTS.length === 4) {
      const WEST = PARTS[0].trim();
      const SOUTH = PARTS[1].trim();
      const EAST = PARTS[2].trim();
      const NORTH = PARTS[3].trim();

      const WEST_SAFE =
        WEST && !isNaN(WEST) ? _getSafeLatLng(WEST, true) : null;
      const SOUTH_SAFE =
        SOUTH && !isNaN(SOUTH) ? _getSafeLatLng(SOUTH, false) : null;
      const EAST_SAFE =
        EAST && !isNaN(WEST) ? _getSafeLatLng(EAST, true) : null;
      const NORTH_SAFE =
        NORTH && !isNaN(WEST) ? _getSafeLatLng(NORTH, false) : null;

      if (WEST_SAFE && SOUTH_SAFE && EAST_SAFE && NORTH_SAFE) {
        return `${WEST_SAFE},${SOUTH_SAFE},${EAST_SAFE},${NORTH_SAFE}`;
      }
    }
  }
  return null;
};

const _getPrecision = (map) => {
  if (map) {
    let precision = Math.floor(map.getZoom()) + 4;
    if (precision < MAP_PRECISIONS.min) {
      precision = MAP_PRECISIONS.min;
    }
    if (precision > MAP_PRECISIONS.max) {
      precision = MAP_PRECISIONS.max;
    }
    return precision;
  }
  return null;
};

const _getFeatureArea = (feature) => {
  let featureArea;
  if (lodashGet(feature, "geometry")) {
    featureArea = area(feature);
  }
  return featureArea;
};

const _getBboxFeatureByBounds = (bounds) => {
  let p1 = typeof bounds[0] === "string" ? parseFloat(bounds[0]) : bounds[0];
  let p2 = typeof bounds[1] === "string" ? parseFloat(bounds[1]) : bounds[1];
  let p3 = typeof bounds[2] === "string" ? parseFloat(bounds[2]) : bounds[2];
  let p4 = typeof bounds[3] === "string" ? parseFloat(bounds[3]) : bounds[3];

  return {
    type: "Feature",
    properties: {},
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [p2, p1],
          [p2, p3],
          [p4, p3],
          [p4, p1],
          [p2, p1],
        ],
      ],
    },
  };
};

const _isBoundsStringEqual = (bounds1, bounds2) => {
  // There is a .0001 precision preventing it from realizing it's the same bounds
  if (!!bounds1 && !!bounds2) {
    let b1 = bounds1.split(",");
    let b2 = bounds2.split(",");

    return (
      parseFloat(b1[0]).toFixed(2) === parseFloat(b2[0]).toFixed(2) &&
      parseFloat(b1[1]).toFixed(2) === parseFloat(b2[1]).toFixed(2) &&
      parseFloat(b1[2]).toFixed(2) === parseFloat(b2[2]).toFixed(2) &&
      parseFloat(b1[3]).toFixed(2) === parseFloat(b2[3]).toFixed(2)
    );
  }
  return true;
};

const _convertPolygonsToMultiPolygon = (features) => {
  let polys = [];
  for (let i = 0; i < features.length; i++) {
    if (features[i].geometry.type === "MultiPolygon") {
      polys.push(features[i].geometry.coordinates[0]);
    } else {
      polys.push(features[i].geometry.coordinates);
    }
  }
  return {
    properties: {},
    type: "Feature",
    geometry: {
      type: "MultiPolygon",
      coordinates: polys,
    },
  };
};
